import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, distinctUntilChanged } from 'rxjs';
import * as FontFaceObserver from 'fontfaceobserver';
import { ThemeService } from './theme.service';

export enum ViewType {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  DESKTOP = 'desktop',
}

export enum LocalDirection {
  LTR = 'ltr',
  RTL = 'rtl',
}

export const emojiLibraryIndex = {
  THUMBS_UP: 0,
  THUMBS_DOWN: 1,
  VICTORY_HANDS: 9,
  HAND_RAISED: 10,
};

export const emojiLibrary = [
  '/assets/icons/emojis/thumbs-up.png',
  '/assets/icons/emojis/thumbs-down.png',
  '/assets/icons/emojis/party-popper.png',
  '/assets/icons/emojis/fire.png',
  '/assets/icons/emojis/clapping-hands.png',
  '/assets/icons/emojis/red-heart.png',
  '/assets/icons/emojis/raising-hands.png',
  '/assets/icons/emojis/folded-hands.png',
  '/assets/icons/emojis/eyes.png',
  '/assets/icons/emojis/victory_hands.png',
  '/assets/icons/emojis/face_tears_of_joy.png',
];

@Injectable({
  providedIn: 'root',
})
export class UiService {
  MOBILE_MAX_WIDTH = 693;
  MOBILE_SINGLE_VIDEO_TILE_SIZE = 320;
  MOBILE_DOUBLE_VIDEO_TILE_SIZE = 280;
  MOBILE_MULTIPLE_VIDEO_TILE_SIZE = 180;
  TABLET_MIN_WIDTH = 694;
  BROWSER_MIN_WIDTH = 700;
  BROWSER_MIN_HEIGHT = 550;
  isMobile = new BehaviorSubject(false);

  private _isPipWindowMobile = new BehaviorSubject(false);
  isPipWindowMobile$ = this._isPipWindowMobile.pipe(distinctUntilChanged());

  get isPipWindowMobile() {
    return this._isPipWindowMobile.getValue();
  }

  set isPipWindowMobile(value: boolean) {
    if (value !== this._isPipWindowMobile.value) {
      this._isPipWindowMobile.next(value);
    }
  }
  constructor(
    private titleService: Title,
    private translateService: TranslateService,
    private themeService: ThemeService,
  ) {}

  setTabTitle(tabName: string, translate = true): void {
    const appName = this.themeService.appName;
    if (translate) {
      this.titleService.setTitle(`${this.translateService.instant(tabName)} | ${appName}`);
    } else {
      this.titleService.setTitle(`${tabName} | ${appName}`);
    }
  }

  /**
   * Load fonts for sessions/whiteboard page
   *
   * All fonts are now loaded via fonts.scss from Google Fonts.
   * This method waits for fonts to be ready using FontFaceObserver.
   */
  async loadFonts() {
    const fonts = [
      new FontFaceObserver('Jost').load(),
      new FontFaceObserver('Poppins').load(),
      new FontFaceObserver('EB Garamond').load(),
      new FontFaceObserver('Caveat').load(),
      new FontFaceObserver('Permanent Marker').load(),
      new FontFaceObserver('Virgil').load(),
      new FontFaceObserver('OpenDyslexic').load(),
      new FontFaceObserver('Source Sans Pro', { weight: 400, style: 'normal' }).load(),
      new FontFaceObserver('Source Sans Pro', { weight: 400, style: 'italic' }).load(),
      new FontFaceObserver('Source Sans Pro', { weight: 600, style: 'normal' }).load(),
      new FontFaceObserver('Source Sans Pro', { weight: 600, style: 'italic' }).load(),
      new FontFaceObserver('Source Sans Pro', { weight: 700, style: 'normal' }).load(),
      new FontFaceObserver('Source Sans Pro', { weight: 700, style: 'italic' }).load(),
    ];
    return Promise.all(fonts)
      .then(() => true)
      .catch(() => false);
  }
}
